<template>
  <div class="min-h-screen bg-gray-50 px-4 pt-6 pb-28" dir="rtl">
    <div class="max-w-4xl mx-auto md:max-w-lg space-y-4">
      
      <!-- Request Items List -->
      <div v-if="list.length > 0" class="space-y-3">
        <div 
          v-for="item in list" 
          :key="item.Id"
          class="bg-white rounded-xl border p-5 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 border-gray-200 hover:border-blue-300"
          @click="goToDetail(item)"
        >
          <div class="flex items-start gap-4">
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div class="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            
            <!-- Content -->
            <div class="flex-1 min-w-0">
              <div class="mb-3">
                <h3 class="text-base font-bold text-gray-900 mb-2">{{ item.summary }}</h3>
                <div class="flex items-center gap-4 text-sm text-gray-600">
                  <span>کد فاکتور: {{ item.document_code }}</span>
                </div>
              </div>
              
              <div class="flex items-center gap-4 pt-3 border-t border-gray-100">
                <!-- Price -->
                <div class="flex items-center gap-2 text-sm">
                  <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="font-bold text-green-600 text-lg">{{ get_currency(item.price) }} <span class="text-sm">تومان</span></span>
                </div>
                
                <!-- Date -->
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{{ item.created_at }}</span>
                </div>
              </div>
            </div>
            
            <!-- Arrow Icon -->
            <div class="flex-shrink-0 self-center">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
        <div class="max-w-2xl mx-auto">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <p class="text-gray-500 font-medium mb-2">فاکتوری وجود ندارد</p>
          <p class="text-sm text-gray-400">برای شروع، روی دکمه زیر کلیک کنید</p>
        </div>
      </div>

      <!-- Add New Factor Button -->
      <div 
        class="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-all duration-200"
        @click="dialog_add_factor=true"
      >
        <div class="flex items-center justify-between group">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <span class="text-gray-900 font-medium">ثبت فاکتور جدید</span>
          </div>
          <svg class="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </div>
      </div>

    </div>

    <!-- Add Factor Modal -->
    <div v-if="dialog_add_factor" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-90vh overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-bold text-gray-900">ثبت فاکتور آنلاین</h2>
        </div>

        <!-- Body -->
        <div class="px-6 py-4 overflow-y-auto flex-1">
          <div class="space-y-4">
            <!-- Price -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">قیمت</label>
              <input 
                type="tel"
                inputmode="numeric"
                pattern="[0-9]*"
                v-model="price"
                placeholder="مثلاً 1,000,000"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style="border: 1px solid #d1d5db !important;"
              />
            </div>

            <!-- Summary -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">توضیحات</label>
              <textarea 
                v-model="summary"
                rows="4"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style="border: 1px solid #d1d5db !important;"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button 
            class="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            @click="dialog_add_factor=false"
          >
            انصراف
          </button>
          <button 
            class="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200"
            @click="add_factor()"
          >
            تایید
          </button>
        </div>
      </div>
    </div>

  </div>
</template>
<script>

import {bus} from "../../main";

export default {
  props: [
    'passing_data'
  ],
  data() {
    return {
      dialog_add_factor: false,
      price: '',
      summary: '',
      list: [],
      errors: [],
      selected_branch: 0,
      show_loading: false,
      no_info: true,
      page: 0,
      request_data: {},
      first: true,
    }
  },
  components: {},
  watch: {
    price() {
      const formatted = this.formatAmountInput(this.price)
      if (this.price !== formatted) {
        this.price = formatted
      }
    },
  },
  mounted() {
    this.request_data = JSON.parse(localStorage.getItem('online_factor_data'))
    this.get_factors()
  },

  methods: {
    goToDetail(item) {
      window.open("https://pay.zoodkomak.com/i/" + item.unique_code)
    },
    get_factors() {
      // this.request_items = [];
      var self = this
      const send_data = {
        document_code: this.request_data.req_code,
      }
      var formData = new FormData();
      formData.append("document_code", this.request_data.req_code)
      // this.axios.post('/Register' , send_data,{
      this.axios.post('https://pay.zoodkomak.com/api/get_document_from_zoodkomak_app', formData, {
        headers: [],
      })
        .then(response => {
          if (response.data.error == 0) {
            self.list = response.data.data
          }
        }).catch(e => {
        this.errors.push(e)
      })
    },
    add_factor() {
      // this.request_items = [];
      var self = this
     let user_phone = localStorage.getItem("phone")
      var user_name = JSON.parse(localStorage.getItem('profile')).UserName

     var customer_name = this.request_data.req_name.split('_')[0]
      var formData = new FormData();
      formData.append("user_mobile","0"+user_phone.substring(3));
      formData.append("user_name",user_name);
      formData.append("customer_mobile", this.request_data.customer_mobile);
      formData.append("customer_name", customer_name);
      formData.append("economic_code", "");
      formData.append("market_code", "13");
      formData.append("address", "");
      formData.append("total_price", this.parseAmountInput(this.price));
      formData.append("expire_hour", 24);
      formData.append("document_code", this.request_data.req_code)
      formData.append("summary", this.summary)
      // this.axios.post('/Register' , send_data,{
      this.axios.post('https://pay.zoodkomak.com/api/add_document_from_zoodkomak_app', formData, {
        headers: [],
      })
        .then(response => {
          self.dialog_add_factor = false
          if (response.data.error == 0) {

            this.get_factors();
            bus.$emit('show_toast', [3000, response.data.msg])

          }else{
            bus.$emit('show_toast', [3000, response.data.msg])
            }
        }).catch(e => {
        this.errors.push(e)
      })
    },
  }
}
</script>

<style scoped>
/* Custom styles */
</style>
